import {t} from "i18next";
import Swal from "sweetalert2";

import {makeSwalShowPassword} from "../makeSwalShowPassword/makeSwalShowPassword";

export const swalCustom = {
  confirm: ({
    confirmFunc = () => {},
    text,
    html,
    icon = "question",
    confirmButtonText = "Yes",
    cancelButtonText = "No",
  }) => {
    Swal.fire({
      title: "",
      text: html ? undefined : t(text) || `${t("Are you sure?")}`,
      html,
      icon,
      confirmButtonText: [t(confirmButtonText)],
      showCancelButton: true,
      cancelButtonText: [t(cancelButtonText)],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc();
      }
    });
  },

  info: ({
    confirmFunc = () => {},
    text = "",
    title = "",
    noTitle,
    confirmButtonText = "Yes",
    showCancelButton = true,
  }) => {
    Swal.fire({
      title: !noTitle ? t(title) || t("Information") : "",
      text: t(text),
      icon: "info",
      confirmButtonText: [t(confirmButtonText)],
      showCancelButton,
      cancelButtonText: [t("No")],
      allowEscapeKey: true,
      allowOutsideClick: true,
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc();
      }
    });
  },

  unsavedData: ({confirmFunc = () => {}, text = "", title = "", cancelFunc = () => {}}) => {
    Swal.fire({
      title: t(title) || t("You have unsaved issues. Close anyway?"),
      text: t(text),
      icon: "warning",
      confirmButtonText: [t("Yes")],
      showCancelButton: true,
      cancelButtonText: [t("No")],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc();
      } else cancelFunc();
    });
  },

  exitConfirm: ({confirmFunc = () => {}, text = "", title = "", cancelFunc = () => {}}) => {
    Swal.fire({
      title: t(title) || t("All unsaved data will be deleted. Close anyway?"),
      text: t(text),
      icon: "warning",
      confirmButtonText: [t("Yes")],
      showCancelButton: true,
      cancelButtonText: [t("No")],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc();
      } else cancelFunc();
    });
  },

  passwordCheck: ({confirmFunc = () => {}, text = "", validateFunc = null}) => {
    const message = t(text) || `${t("Enter your password to continue")}`;

    Swal.fire({
      ...makeSwalShowPassword({placeholder: t("Your account password")}),
      showLoaderOnConfirm: true,
      title: message,
      confirmButtonText: [t("Confirm ")],
      showCancelButton: true,
      cancelButtonText: [t("Cancel")],
      preConfirm: async () => {
        const password = document.getElementById("swal-password-input")?.value?.trim();

        if (!password) {
          Swal.showValidationMessage(t("You need to enter your password"));
          return false;
        }

        if (typeof validateFunc === "function") {
          const isValid = await validateFunc(password);

          if (!isValid) {
            Swal.showValidationMessage(t("Current password is incorrect"));
            return false;
          }
        }

        return password;
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc(answ.value);
      }
    });
  },

  newPassword: ({html, confirmFunc = () => {}}) => {
    Swal.fire({
      html:
        html ||
        `<input type="password" id="swal-input1" class="swal2-input" placeHolder="${t(
          "New password",
        )}"/><input type="password" id="swal-input2" class="swal2-input"
        placeHolder="${t("Confirm password")}"
        />`,
      preConfirm: () => {
        const password = document.querySelector("#swal-input1").value;
        const passwordConfirm = document.querySelector("#swal-input2").value;

        if (!password || !passwordConfirm) {
          Swal.showValidationMessage(t("You need to enter your password"));
          return;
        }
        if (password !== passwordConfirm) {
          Swal.showValidationMessage(t("Passwords do not match!"));
          return;
        }

        return {
          password,
          passwordConfirm,
        };
      },
      showLoaderOnConfirm: true,
      confirmButtonText: [t("Confirm ")],
      showCancelButton: true,
      cancelButtonText: [t("Cancel")],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc(answ.value);
      }
    });
  },

  twoInputPopUp: ({
    nameFirst = "first",
    labelFirst = "First",
    nameSecond = "second",
    labelSecond = "Second",
    confirmFunc = () => {},
    validateFunc = null,
    title = "",
    required = true,
  }) => {
    Swal.fire({
      title: t(title),
      html: `<div style="display: flex; flex-direction: row; flex-wrap: nowrap; align-items: baseline; justify-content: space-between; gap: 12px;"><div style="display: flex; flex-direction: row; flex-wrap: nowrap; white-space: nowrap;">${t(
        labelFirst,
      )}${required ? "*" : ""}:</div><input id="swal-input1" class="swal2-input" style="padding: 0 0.5rem;"
        /></div><div style="display: flex; flex-direction: row; align-items: baseline; justify-content: space-between; gap: 12px;"><div style="display: flex; flex-direction: row; flex-wrap: nowrap; white-space: nowrap;">${t(
          labelSecond,
        )}${required ? "*" : ""}:</div><input id="swal-input2" class="swal2-input" style="padding: 0 0.5rem;"
        /></div>`,
      preConfirm: async () => {
        const firstInput = document.querySelector("#swal-input1");
        const secondInput = document.querySelector("#swal-input2");
        const firstValue = firstInput?.value;
        const secondValue = secondInput?.value;

        if (firstInput) firstInput.style.borderColor = "";
        if (secondInput) secondInput.style.borderColor = "";

        if (required && (!firstValue || !secondValue)) {
          Swal.showValidationMessage(t("You need to fill both fields"));
          return false;
        }

        if (typeof validateFunc === "function") {
          const validationResult = await validateFunc({
            [nameFirst]: firstValue,
            [nameSecond]: secondValue,
          });

          if (validationResult === false) {
            Swal.showValidationMessage(t("Validation failed"));
            return false;
          }

          if (validationResult?.isValid === false) {
            const invalidFields =
              validationResult?.invalidFields ||
              (validationResult?.invalidField ? [validationResult.invalidField] : []);

            if (invalidFields.includes("first") && firstInput) {
              firstInput.style.borderColor = "#f27474";
            }

            if (invalidFields.includes("second") && secondInput) {
              secondInput.style.borderColor = "#f27474";
            }

            Swal.showValidationMessage(t(validationResult?.message || "Validation failed"));
            return false;
          }
        }

        return {
          [nameFirst]: firstValue,
          [nameSecond]: secondValue,
        };
      },
      showLoaderOnConfirm: true,
      confirmButtonText: [t("Confirm ")],
      showCancelButton: true,
      cancelButtonText: [t("Cancel")],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc(answ.value);
      }
    });
  },

  optionalContactDetails: ({confirmFunc = () => {}, initialValues = {}} = {}) => {
    Swal.fire({
      title: "",
      html: `<div style="display: flex; flex-direction: column; gap: 16px; text-align: left;">
        <label for="swal-phone-input">${t("Phone")}:</label>
        <input id="swal-phone-input" type="tel" class="swal2-input" style="box-sizing: border-box; margin: 0; width: 100%;" />
        <label for="swal-text-input">${t("Notes")}:</label>
        <textarea id="swal-text-input" class="swal2-textarea" style="box-sizing: border-box; margin: 0; width: 100%; resize: vertical;"></textarea>
      </div>`,
      focusConfirm: false,
      didOpen: () => {
        const phoneInput = document.querySelector("#swal-phone-input");
        const textInput = document.querySelector("#swal-text-input");

        if (phoneInput) phoneInput.value = initialValues?.phone ?? "";
        if (textInput) textInput.value = initialValues?.text ?? "";
      },
      preConfirm: () => ({
        text: document.querySelector("#swal-text-input")?.value?.trim() || "",
        phone: document.querySelector("#swal-phone-input")?.value?.trim() || "",
      }),
      confirmButtonText: [t("Ok")],
      showCancelButton: true,
      cancelButtonText: [t("Cancel")],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc(answ.value);
      }
    });
  },
  confirmWithNote: ({confirmFunc = () => {}}) => {
    Swal.fire({
      title: "",
      html: `<div style="display: flex; flex-direction: column; gap: 16px; text-align: left;">
        <label for="swal-note-input">${t("Note")}:</label>
        <input id="swal-note-input" type="text" class="swal2-input" style="box-sizing: border-box; margin: 0; width: 100%;" />
      </div>`,
      focusConfirm: false,
      preConfirm: () => ({
        note: document.querySelector("#swal-note-input")?.value?.trim() || "",
      }),
      confirmButtonText: [t("Ok")],
      showCancelButton: true,
      cancelButtonText: [t("Cancel")],
    }).then(answ => {
      if (answ.isConfirmed) {
        confirmFunc(answ.value);
      }
    });
  },
};
